import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../common/dialog/BootstrapDialog';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { listChanceEstimates } from '../../../modules/chanceModule/chanceEstimates';

import CostCalcListContainer from '../../../containers/chancemng/estimate/CostCalcListContainer';
import DetailTemplate from '../../common/template/DetailTemplate';
import EstimateFormContainer from '../../../containers/chancemng/estimate/EstimateFormContainer';
import PurchseQuoteFileListContainer from '../../../containers/chancemng/estimate/PurchseQuoteFileListContainer';
import SalesQuoteFileListContainer from '../../../containers/chancemng/estimate/SalesQuoteFileListContainer';
import _ from 'lodash';
import * as chanceApi from '../../../lib/api/chance';
import * as fileApi from '../../../lib/api/file';
import { listPurchaseQuoteFiles } from '../../../modules/chanceModule/purchaseQuoteFiles';
import Draggable from 'react-draggable';
import { chanceEstimateActive } from '../../../modules/chanceModule/chance';

function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-estimate-dialog"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const EstimateModal = ({
  chance,
  type,
  estimate,
  open,
  handleClose,
  orderCount,
  vendorEmps,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('세부사항');
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  //파일명 확인
  const downloadFile = (file, filename, filedoc) => {
    let blob = new Blob([file.data]);
    let fileName = `${filename}.${filedoc}`;

    if (window.navigator.msSaveOrOpenBlob) {
      // IE 10+는 해당 함수 제공
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.target = '_self';
      if (fileName) link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(link); //메모리 누수 방지
    }
  };

  // PDF 미리보기/다운로드 (Excel → PDF, 서버/Gotenberg 변환 — 2026-10-20 ksm 포팅).
  // 이전의 html2canvas+jsPDF 클라이언트 캔버스 렌더링은 탭 프리즈 버그(bito 이슈 #10)가 있어 제거함.
  const handlePdfPreview = async () => {
    setPdfPreviewOpen(true);
    setPdfLoading(true);
    try {
      const res = await chanceApi.downloadQuotePdf(estimate.qteUuid);
      const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(pdfUrl);
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data?.message || 'PDF 변환 서비스에 연결할 수 없습니다.'
          : err.message;
      enqueueSnackbar('PDF 미리보기 실패: ' + message, {
        variant: 'error',
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfPreviewClose = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  const handlePdfDownload = async () => {
    try {
      const res = await chanceApi.downloadQuotePdf(estimate.qteUuid);
      const filename = res.headers['filename']
        ? decodeURIComponent(res.headers['filename'])
        : `${estimate.qteNo || 'estimate'}.pdf`;

      downloadFile(res, filename.replace(/\.pdf$/, ''), 'pdf');
      enqueueSnackbar('PDF 다운로드 완료', {
        variant: 'success',
      });
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data?.message || 'PDF 변환 서비스에 연결할 수 없습니다.'
          : err.message;
      enqueueSnackbar('PDF 다운로드 실패: ' + message, {
        variant: 'error',
      });
    }
  };

  const handleExcel = async () => {
    try {
      const blob = await chanceApi.downloadExcel(estimate.qteUuid);
      const filename = blob.headers['filename'];

      downloadFile(blob, decodeURIComponent(filename), 'xlsx');
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const handleDelete = async () => {
    //console.log(data.prjUuid, data.qteUuid);
    try {
      const res = await chanceApi.removeEstimate(
        estimate.prjUuid,
        estimate.qteUuid,
      );
      if (res.data) {
        dispatch(listChanceEstimates(chance.prjUuid));
        dispatch(chanceEstimateActive(chance.prjUuid));
      }
      enqueueSnackbar('견적 삭제 성공', {
        variant: 'success',
      });
      handleClose();
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const validationSchema = yup.object({
    qteRegDt: yup
      .date()
      .required('견적일자를 입력하세요')
      .nullable()
      .default(undefined),
    qteValidDt: yup
      .date()
      .required('견적유효일을 입력하세요')
      .nullable()
      .default(undefined),
    mntTor: yup.number('숫자만 가능합니다'),
    destPlace: yup.string().required('납품 장소를 입력하세요'),
    qteCondt: yup.string().required('발행 조건을 입력하세요'),
    payCond: yup.string().required('결제 조건을 입력하세요'),
    qteDesc1: yup.string().required('견적서 설명 1을 입력하세요'),
    qteUsrNm: yup.string().required('담당자명을 입력하세요'),
    qteUsrTel: yup.string().required('담당자 연락처를 입력하세요'),
    qteUsrEml: yup
      .string()
      .email('잘못된 이메일 형식입니다')
      .required('담당자 이메일을 입력하세요'),
  });

  const [initialValues, setInitialValues] = useState({
    qtePrice:
      estimate && estimate?.qtePrice !== undefined
        ? estimate?.qtePrice
        : chance?.expSales,
    qteRegDt:
      estimate !== null
        ? estimate?.qteRegDt
          ? estimate?.qteRegDt
          : estimate?.crtDt
        : new Date(),
    qteValidDt: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    mntTor: 12,
    destPlace: '고객이 지정한 장소',
    qteCondt: '검수후 발행',
    payCond: '현금',
    qteDesc1:
      '해당 견적서의 직인은 생략하며, 직인 필요시 별도 문의 부탁 드립니다.',
    qteDesc2: '',
    qteDesc3: '',
    qteUsrNm: '',
    qteUsrTel: '',
    qteUsrEml: '',
    crtDt: new Date(),
    files: [{ fileDesc: '', fileTyp: '', file: null }],
    // salesFiles: [{ fileDesc: '', file: null }],
  });

  useEffect(() => {
    if (estimate) {
      const detailTitle = '견적서 번호: ' + estimate?.qteNo + ' 상세 사항';
      setTitle(detailTitle);

      const copyEstimate = _.clone(estimate);
      const qteRegDt =
        estimate !== null
          ? estimate?.qteRegDt
            ? estimate?.qteRegDt
            : estimate?.crtDt
          : new Date();

      const init = {
        ...copyEstimate,
        qteRegDt,
        files: [{ fileDesc: '', file: null }],
        // salesFiles: [{ fileDesc: '', file: null }],
      };
      setInitialValues(init);
    }
  }, [estimate, type]);

  useEffect(() => {
    if (type === 'add' && vendorEmps && vendorEmps.length > 0) {
      const qteUsrNm = vendorEmps[0]?.employeeNm;
      const qteUsrTel = vendorEmps[0]?.telNo;
      const qteUsrEml = vendorEmps[0]?.emailAddr;
      const copyInit = _.clone(initialValues);
      const changeInit = { ...copyInit, qteUsrNm, qteUsrTel, qteUsrEml };
      setInitialValues(changeInit);
    }
  }, [type, vendorEmps]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
      handleClose();
    },
  });

  const onFileSave = async (value, estimate) => {
    let formData = new FormData();
    formData.append('fileDt', value.file);
    formData.append('fileTyp', 'project');
    formData.append('fileAttrVal1', estimate.prjUuid);
    formData.append('fileAttrVal2', value.fileTyp);
    formData.append('fileAttrVal3', estimate.qteUuid);
    formData.append('fileDesc', value.fileDesc);

    try {
      const response = await fileApi.addFiles(formData);

      if (response.data.result === 'success') {
        enqueueSnackbar('파일 업로드 성공', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const onSave = async (values) => {
    let estimateToSubmit = {
      prjUuid: chance.prjUuid,
      //qtePrice:chance.expSales,//총 견적금액.확인 필요함.
      qteTyp: chance?.prjTyp === 'M' ? 'B' : 'A', //영업기회 종류 : 프로젝트면 A, 유지보수면 B로 고정
      qteKind: 'S', //매출
      qtePrice: values.qtePrice,
      qteRegDt: dayjs(values.qteRegDt).format('YYYY-MM-DD'),
      qteValidDt: values.qteValidDt
        ? dayjs(values.qteValidDt).format('YYYY-MM-DD')
        : dayjs().add(30, 'day').format('YYYY-MM-DD'),
      mntTor: values.mntTor,
      destPlace: values.destPlace,
      payCond: values.payCond,
      qteCondt: values.qteCondt,
      qteDesc1: values.qteDesc1,
      qteDesc2: values.qteDesc2,
      qteDesc3: values.qteDesc3,
      qteUsrNm: values.qteUsrNm, //담당자명
      qteUsrTel: values.qteUsrTel, //담당자 연락처
      qteUsrEml: values.qteUsrEml, //담당자 이메일
    };

    if (type === 'add') {
      try {
        const res = await chanceApi.addEstimate(estimateToSubmit);

        if (res.data) {
          //실제 업로드 파일이 있다면
          if (values.files.length > 0 && values.files[0].file !== null) {
            values.files.forEach((value) => {
              onFileSave(value, res.data);
            });
          }

          // if (values.salesFiles.length > 0 && values.salesFiles[0].file !== null) {
          //   values.salesFiles.forEach((value) => {
          //     onFileSave(value, res.data, '매출');
          //   });
          // }

          dispatch(listChanceEstimates(chance.prjUuid));
          dispatch(chanceEstimateActive(chance.prjUuid));
        }
        enqueueSnackbar('견적 등록 성공', {
          variant: 'success',
        });
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response?.estimate?.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    } else {
      try {
        estimateToSubmit.qteUuid = estimate.qteUuid;
        const res = await chanceApi.editEstimate(estimateToSubmit);
        if (res.data.info) {
          if (values.files.length > 0 && values.files[0].file !== null) {
            values.files.forEach((value) => {
              onFileSave(value, res.data.info);
              dispatch(
                listPurchaseQuoteFiles(
                  'project',
                  res.data.info.prjUuid,
                  res.data.info.qteUuid,
                ),
              );
            });
          }
          // if (values.salesFiles.length > 0 && values.salesFiles[0].file !== null) {
          //   values.salesFiles.forEach((value) => {
          //     onFileSave(value, res.data.info, '매출');
          //     dispatch(
          //       listPurchaseQuoteFiles(
          //         'project',
          //         res.data.info.prjUuid,
          //         res.data.info.qteUuid,
          //       ),
          //     );
          //   });
          // }

          dispatch(listChanceEstimates(chance.prjUuid));
          dispatch(chanceEstimateActive(chance.prjUuid));
        }
        enqueueSnackbar('견적 수정 성공', {
          variant: 'success',
        });
      } catch (err) {
        let message =
          typeof err.response !== 'undefined'
            ? err.response.estimate.message
            : err.message;
        enqueueSnackbar(message, {
          variant: 'error',
        });
      }
    }
  };

  if (!chance) {
    return null;
  }

  return (
    <BootstrapDialog
      open={open}
      onClose={handleClose}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-estimate-dialog"
      fullWidth={true}
      maxWidth="lg"
    >
      <BootstrapDialogTitle
        style={{ cursor: 'move' }}
        id="draggable-estimate-dialog"
        onClose={handleClose}
      >
        견적서 - [{chance?.prjNo}]{chance?.cstNm}
      </BootstrapDialogTitle>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DialogContent
          dividers
          sx={{
            height: 500,
            overflowY: 'auto',
          }}
        >
          <DetailTemplate title={title}>
            <EstimateFormContainer
              formik={formik}
              type={type}
              chance={chance}
              estimate={estimate}
              handleClose={handleClose}
            />
          </DetailTemplate>

          {/* 2026-10-23 (250423 피드백 #12): 매출/매입 견적서 참조 파일을 하나의
              리스트(매입 모듈 재사용)에 섞어 보여주던 걸 분리했다. */}
          {type === 'edit' && (
            <DetailTemplate title="매출 견적서 참조 파일">
              <SalesQuoteFileListContainer estimate={estimate} />
            </DetailTemplate>
          )}

          {type === 'edit' && (
            <DetailTemplate title="매입 견적서 참조 파일">
              <PurchseQuoteFileListContainer estimate={estimate} />
            </DetailTemplate>
          )}

          <DetailTemplate title="원가산출 리스트">
            <CostCalcListContainer />
          </DetailTemplate>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <>
            <Box>
              {orderCount === 0 ? (
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleDelete}
                >
                  삭제
                </Button>
              ) : (
                <Typography variant="subtitle2" color="error.main">
                  현재 Active한 수주품의서가 있어, 견적서 삭제 불가합니다.
                </Typography>
              )}
            </Box>
            {type === 'edit' && estimate?.useYn === 'Y' && (
              <Box>
                <Button
                  sx={{ mr: 2 }}
                  color="info"
                  variant="contained"
                  onClick={handlePdfPreview}
                >
                  PDF 미리보기
                </Button>

                <Button
                  sx={{ mr: 2 }}
                  color="success"
                  variant="contained"
                  onClick={handlePdfDownload}
                >
                  PDF 다운로드
                </Button>

                <Button
                  sx={{ mr: 2 }}
                  color="secondary"
                  variant="contained"
                  onClick={handleExcel}
                >
                  엑셀
                </Button>
              </Box>
            )}
          </>
          {/* type === 'add' 일 때는 아직 견적서가 저장되지 않아 서버 PDF 미리보기를 쓸 수 없음 */}
        </DialogActions>
      </Box>
      {/* PDF 미리보기 모달 (서버 Gotenberg 변환) */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={handlePdfPreviewClose}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          PDF 미리보기
          <IconButton onClick={handlePdfPreviewClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            height: '80vh',
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {pdfLoading ? (
            <CircularProgress />
          ) : pdfPreviewUrl ? (
            <iframe
              src={pdfPreviewUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="견적서 PDF 미리보기"
            />
          ) : (
            <Typography color="text.secondary">
              PDF를 불러올 수 없습니다. PDF 변환 서비스(Gotenberg)가 실행
              중인지 확인하세요.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </BootstrapDialog>
  );
};

export default EstimateModal;
